import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { laseaAuthService } from '../../services/laseaAuthService';

const SECURITY_QUESTIONS = [
  "What is your mother's maiden name?",
  "What city were you born in?",
  "What is the name of your first pet?",
  "What is your favorite book?",
  "What high school did you attend?",
  "In what city or town did your mother and father meet?"
];

export default function ClientRegistration() {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    companyName: '',
    phone: '',
    securityQuestion: SECURITY_QUESTIONS[0],
    securityAnswer: ''
  });
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (formData?.password !== formData?.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData?.password?.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (!formData?.securityAnswer?.trim()) {
      setError('Please answer the security question');
      setLoading(false);
      return;
    }

    try {
      await laseaAuthService?.signUpCustomer(
        formData?.email,
        formData?.password,
        formData?.fullName,
        formData?.companyName,
        formData?.phone,
        formData?.securityQuestion,
        formData?.securityAnswer
      );

      // Try to auto-login the user after signup
      try {
        await laseaAuthService?.signIn(formData?.email, formData?.password);
        navigate('/client-dashboard');
      } catch (loginErr) {
        // If auto-login fails, redirect to login page
        setSuccess(true);
        setTimeout(() => {
          navigate('/client-login');
        }, 2000);
      }
    } catch (err) {
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center min-h-screen bg-gray-900 p-6">
      <div className="w-full max-w-2xl bg-gray-800 rounded-lg shadow-md p-8">
        <h2 className="text-2xl font-bold text-center mb-6 text-white">Customer Registration</h2>
        
        {error && (
          <div className="mb-4 p-3 bg-red-900 border border-red-700 text-red-200 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-900 border border-green-700 text-green-200 rounded">
            Registration successful! Redirecting to login...
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Account Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-white">Account Information</h3>
            
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Email *</label>
              <input
                type="email"
                name="email"
                value={formData?.email}
                onChange={handleInputChange}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Password *</label>
              <input
                type="password"
                name="password"
                value={formData?.password}
                onChange={handleInputChange}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
                minLength={6}
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Confirm Password *</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData?.confirmPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Personal Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-white">Personal Information</h3>
            
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Full Name *</label>
              <input
                type="text"
                name="fullName"
                value={formData?.fullName}
                onChange={handleInputChange}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Company Name</label>
              <input
                type="text"
                name="companyName"
                value={formData?.companyName}
                onChange={handleInputChange}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData?.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              />
            </div>
          </div>

          {/* Security Question for Account Recovery */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-white">Security Question</h3>
            <p className="text-sm text-gray-400 mb-4">Answer this question to help recover your account if you forget your password</p>
            
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Security Question *</label>
              <select
                name="securityQuestion"
                value={formData?.securityQuestion}
                onChange={handleInputChange}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              >
                {SECURITY_QUESTIONS.map((question, index) => (
                  <option key={index} value={question}>
                    {question}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Your Answer *</label>
              <input
                type="text"
                name="securityAnswer"
                value={formData?.securityAnswer}
                onChange={handleInputChange}
                placeholder="Enter your answer (case-insensitive)"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">Example: If the question is about your pet's name, enter just the name</p>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
          >
            {loading ? 'Creating Account...' : 'Register'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={() => navigate('/client-login')}
            className="text-blue-400 hover:text-blue-300 transition"
          >
            Already have an account? Sign in
          </button>
        </div>
      </div>
    </div>
  );
}